import React, { useMemo } from "react";
import { Ban, Plane, Sparkles, TrendingUp } from "lucide-react";
import { Skeleton } from "../../../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../ui/table";
import { cn } from "../../../lib/utils";
import { EmptyState, InsightCard, InsightPill, SectionHeading } from "../components/Primitives";
import { BarList, CellBar, MiniStack, ShareBar } from "../components/DataBars";
import { StakeMix } from "../components/StakeMix";
import { humanise, pct } from "../utils/format";
import { useMoney } from "../utils/currency";
import { deriveCategoryMix, deriveGames, dominantCrashGame, riskLevelGames } from "../utils/derive";

/**
 * Section C — Casino DNA (§4.4). What this player plays, how they stake it, and
 * how they behave inside their dominant game.
 */

function CategoryMix({ categories }) {
  const mix = deriveCategoryMix(categories);
  if (!mix.length) return <EmptyState minHeight={92} message="No casino play recorded yet." />;
  return <ShareBar slices={mix} height={12} />;
}

function TopGamesTable({ games }) {
  const { symbol, amount } = useMoney();
  if (!games.length) return <EmptyState minHeight={180} message="No casino play recorded yet." />;

  const maxShare = Math.max(...games.map((game) => game.overall_bet_share_pct || 0));

  return (
    <div className="-mx-4 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-8 px-4 text-xs font-medium">Game</TableHead>
            <TableHead className="h-8 px-3 text-xs font-medium">Provider</TableHead>
            <TableHead className="h-8 px-3 text-xs font-medium">Category</TableHead>
            <TableHead className="h-8 px-3 text-xs font-medium">% all bets</TableHead>
            <TableHead className="h-8 px-3 text-right text-xs font-medium">% of category</TableHead>
            <TableHead className="h-8 px-4 text-right text-xs font-medium">Typical stakes ({symbol})</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {games.map((game, index) => {
            // Row one carries most of this player's behaviour, so it is weighted
            // visually rather than left to blend into a uniform list.
            const dominant = index === 0;
            return (
              <TableRow key={game.id} className={cn("border-b last:border-0", dominant && "bg-primary/5")}>
                <TableCell className={cn("px-4 py-2 text-sm", dominant ? "font-semibold text-foreground" : "text-foreground")}>
                  <span className="flex items-center gap-1.5">
                    {dominant && <Plane className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />}
                    {game.name}
                  </span>
                </TableCell>
                <TableCell className="px-3 py-2 text-sm text-muted-foreground">{game.provider}</TableCell>
                <TableCell className="px-3 py-2 text-sm text-muted-foreground">{game.category}</TableCell>
                <TableCell className="px-3 py-2">
                  <CellBar
                    value={game.overall_bet_share_pct || 0}
                    max={maxShare}
                    display={pct(game.overall_bet_share_pct)}
                    emphasis={dominant}
                  />
                </TableCell>
                <TableCell className="px-3 py-2 text-right text-sm tabular-nums text-muted-foreground">
                  {pct(game.category_bet_share_pct)}
                </TableCell>
                <TableCell className="px-4 py-2 text-right text-sm tabular-nums text-muted-foreground">
                  {(game.preferred_stakes_kes || []).map(amount).join(" / ") || "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** Reads the cashout curve and says what it means, rather than only drawing it. */
function cashoutInsight(ranges) {
  if (!ranges || !ranges.length) return null;
  const early = ranges
    .filter((range) => /^1\.1x/.test(range.range) || /^1\.5x-1/.test(range.range))
    .reduce((sum, range) => sum + (range.pct || 0), 0);
  const tail = ranges
    .filter((range) => range.range.includes("+"))
    .reduce((sum, range) => sum + (range.pct || 0), 0);

  if (!early && !tail) return null;
  const parts = [];
  if (early >= 40) parts.push(`${early >= 50 ? "half" : `${early}%`} of rounds exit at or below 1.5x`);
  if (tail) parts.push(`${tail === 10 ? "1 in 10" : `${tail}%`} rides past 30x`);
  if (!parts.length) return null;
  return `Early casher — ${parts.join(", but ")}.`;
}

function CashoutProfile({ game, riskGames }) {
  if (!game) return <EmptyState minHeight={160} message="No crash play recorded yet." />;

  const rows = game.cashout_ranges.map((range) => ({
    label: range.range.replace(/x-/g, "x – "),
    value: range.pct,
    display: pct(range.pct),
    tip: `${range.range} · ${pct(range.pct)} of ${game.name} rounds`,
  }));

  const insight = cashoutInsight(game.cashout_ranges);

  return (
    <div className="space-y-3">
      <BarList rows={rows} max={100} labelWidth={78} valueWidth={36} colorClass="bg-primary" />
      {insight && (
        <InsightPill icon={TrendingUp} tone="accent">
          {insight}
        </InsightPill>
      )}

      {riskGames.length > 0 && (
        <div className="border-t pt-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-foreground">Plinko risk level</span>
            <span className="flex items-center gap-2.5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-sm bg-primary" aria-hidden="true" />
                Low
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-sm bg-chart-sports" aria-hidden="true" />
                Med
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-sm bg-chart-sports/40" aria-hidden="true" />
                High
              </span>
            </span>
          </div>
          <div className="space-y-2">
            {riskGames.map((riskGame) => (
              <div key={riskGame.id} className="flex items-center gap-3">
                <span className="w-[76px] shrink-0 truncate text-xs text-muted-foreground" title={riskGame.provider}>
                  {riskGame.provider}
                </span>
                <div className="min-w-0 flex-1">
                  <MiniStack parts={riskGame.risk_levels.map((level) => ({ label: level.level, pct: level.pct }))} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileChips({ tags, openToNewGames }) {
  if (!tags?.length && openToNewGames === null) {
    return <EmptyState minHeight={112} message="No play profile yet." />;
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-2 text-xs text-muted-foreground">Play tags</div>
        <div className="flex flex-wrap gap-1.5">
          {(tags || []).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-md border bg-muted px-2 py-1 text-xs text-foreground"
            >
              {humanise(tag)}
            </span>
          ))}
          {!tags?.length && <span className="text-xs text-muted-foreground">None recorded</span>}
        </div>
      </div>

      {openToNewGames !== null && openToNewGames !== undefined && (
        <InsightPill icon={openToNewGames ? Sparkles : Ban} tone="neutral">
          {openToNewGames
            ? "Open to new titles — a good candidate for game-discovery promotions."
            : "Sticks to favourites — low appetite for new games."}
        </InsightPill>
      )}
    </div>
  );
}

export function CasinoDna({ profile, loading }) {
  const games = useMemo(() => deriveGames(profile?.casino?.categories), [profile]);
  const crashGame = useMemo(() => dominantCrashGame(games), [games]);
  const riskGames = useMemo(() => riskLevelGames(games), [games]);

  const topGame = games[0];
  const concentration =
    topGame && topGame.overall_bet_share_pct >= 50
      ? `${topGame.name} is ${pct(topGame.overall_bet_share_pct)} of everything this player does.`
      : null;

  if (loading) {
    return (
      <section>
        <SectionHeading id="pi-casino" eyebrow="Section C" title="Casino DNA" />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          {[0, 1, 2].map((index) => (
            <div key={index} className="rounded-lg border bg-card p-4 xl:col-span-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-4 h-28 w-full rounded-md" />
            </div>
          ))}
          <div className="rounded-lg border bg-card p-4 xl:col-span-8">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="mt-4 h-64 w-full rounded-md" />
          </div>
          <div className="rounded-lg border bg-card p-4 xl:col-span-4">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="mt-4 h-64 w-full rounded-md" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <SectionHeading
        id="pi-casino"
        eyebrow="Section C"
        title="Casino DNA"
        description="What they play, at what stakes, and how they behave inside their dominant game."
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-12">
        <InsightCard className="xl:col-span-4" title="Category mix" subtitle="Share of casino bets">
          <CategoryMix categories={profile?.casino?.categories} />
        </InsightCard>

        <InsightCard className="xl:col-span-4" title="Stake mix" subtitle="Share of casino bets by stake size">
          <StakeMix stakes={profile?.casino?.preferred_stakes_kes} colorClass="bg-primary" />
        </InsightCard>

        <InsightCard className="md:col-span-2 xl:col-span-4" title="Play profile">
          <ProfileChips
            tags={profile?.casino?.game_tags}
            openToNewGames={profile?.casino?.open_to_new_games ?? null}
          />
        </InsightCard>

        <InsightCard
          className="md:col-span-2 xl:col-span-8"
          title="Top games"
          subtitle="Sorted by share of all bets"
          bodyClassName="px-4 pb-4"
        >
          {concentration && (
            <InsightPill icon={Plane} tone="accent" className="mb-3">
              {concentration}
            </InsightPill>
          )}
          <TopGamesTable games={games} />
        </InsightCard>

        <InsightCard
          className="md:col-span-2 xl:col-span-4"
          title="Cashout profile"
          subtitle={crashGame ? `${crashGame.name} · ${crashGame.provider}` : "Crash games"}
        >
          <CashoutProfile game={crashGame} riskGames={riskGames} />
        </InsightCard>
      </div>
    </section>
  );
}
